import React, { useEffect, useState } from "react";
import Loader from "../components/Loader";
import Calling from "../api/Calling";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MainButton from "../components/ul/MainButton";
import { BiCalendarWeek } from "react-icons/bi";
import { MdDateRange, MdPreview, MdToday } from "react-icons/md";
import { GiClick } from "react-icons/gi";
import { formatView } from "../components/formatView";
import { ClickChart, ViewChart } from "../components/charts/ViewClickChart";
import { useSelector } from "react-redux";
import { SiGoogleads } from "react-icons/si";
import { TbCalendarStats } from "react-icons/tb";
import { BsCalendarMonth, BsCalendarRange } from "react-icons/bs";

const Home = () => {
  const [loader, setLoader] = useState(false);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [viewsData, setViewsData] = useState([]);
  const [clicksData, setClicksData] = useState([]);
  const [totalViews, setTotalViews] = useState(0);
  const [totalClicks, setTotalClicks] = useState(0);
  const [totalAds, setTotalAds] = useState(0);
  const [selectState, setSelectState] = useState("today");
  const profile = useSelector((state) => state.profile);
  const { id } = useParams();
  const navigate = useNavigate();
  const fetchData = async () => {
    setLoader(true);
    try {
      let response = {};
      if (id) {
        response = await Calling.adminDashbordWithId(id, startDate, endDate);
      } else {
        response = await Calling.adminDashbord(
          profile?._id,
          startDate,
          endDate
        );
      }
      if (response?.status) {
        setViewsData(response?.views_data);
        setClicksData(response?.clicks_data);
        setTotalClicks(response?.totalClicks || 0);
        setTotalViews(response?.totalViews || 0);
        setTotalAds(response?.totalAds || 0);
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const handleDateRangeFilter = (range) => {
    const today = new Date();
    let start, end;

    switch (range) {
      case "today":
        start = today.toISOString().split("T")[0];
        end = start;
        setSelectState("today");
        break;
      case "yesterday":
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);
        start = yesterday.toISOString().split("T")[0];
        end = start;
        setSelectState("yesterday");
        break;
      case "thisWeek":
        const firstDayOfWeek = new Date(
          today.setDate(today.getDate() - today.getDay())
        );
        const lastDayOfWeek = new Date(firstDayOfWeek);
        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
        start = firstDayOfWeek.toISOString().split("T")[0];
        end = lastDayOfWeek.toISOString().split("T")[0];
        setSelectState("thisWeek");
        break;
      case "thisMonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split("T")[0];
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
          .toISOString()
          .split("T")[0];
        setSelectState("thisMonth");
        break;
      case "last6Months":
        start = new Date(today.getFullYear(), today.getMonth() - 5, 1)
          .toISOString()
          .split("T")[0];
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0)
          .toISOString()
          .split("T")[0];
        setSelectState("last6Months");
        break;
      case "thisYear":
        start = new Date(today.getFullYear(), 0, 1).toISOString().split("T")[0];
        end = new Date(today.getFullYear(), 11, 31).toISOString().split("T")[0];
        setSelectState("thisYear");
        break;
      default:
        start = "";
        end = "";
    }

    setStartDate(start);
    setEndDate(end);
  };

  const handleFormDate = (e) => {
    if (e.target.name === "startDate") {
      setStartDate(e.target.value);
      setSelectState("range");
    } else {
      setEndDate(e.target.value);
      setSelectState("range");
    }
  };

  const handelDeleteTrack = async () => {
    const content = `You are about to delete a ads views and clicks data. To confirm, please solve this math problem.`;
    const userAnswer = prompt(`${content} Enter 'admin' to confirm:`);

    if (!userAnswer || userAnswer.trim().toLowerCase() !== "admin") {
      toast.error("Deletion not confirmed. Please type 'admin' to proceed.");
      return;
    }

    setLoader(true);
    try {
      // Call delete API
      const response = await Calling.deleteAdsTrack(id);

      if (response?.status) {
        toast.success(
          response?.message || "Advertisement(s) Track deleted successfully."
        );
        fetchData(); // Refresh data
      } else {
        toast.error(
          response?.message || "Failed to delete advertisement(s). Track"
        );
      }
    } catch (error) {
      console.error("Error deleting advertisements:", error);
      toast.error("An error occurred while deleting advertisement(s).");
    } finally {
      setLoader(false);
    }
  };
  // Date filter options with their icons
  const dateFilterOptions = [
    { id: "today", label: "Today", icon: <MdToday /> },
    { id: "yesterday", label: "Yesterday", icon: <MdDateRange /> },
    { id: "thisWeek", label: "This Week", icon: <BiCalendarWeek /> },
    { id: "thisMonth", label: "This Month", icon: <BsCalendarMonth /> },
    { id: "last6Months", label: "Last 6 Months", icon: <TbCalendarStats /> },
    { id: "thisYear", label: "This Year", icon: <BsCalendarRange /> },
  ];

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="w-full text-main-font mt-24 px-3 sm:px-6 max-w-screen-2xl mx-auto">
          <div
            className="bg-main-white rounded-2xl shadow-lg h-auto
         transition-all duration-300 hover:shadow-xl"
          >
            {/* Dashboard Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-main-border rounded-tr-2xl rounded-tl-2xl">
              <h1 className="text-2xl sm:text-3xl font-bold text-blue-800">
                Dashboard
                <span className="ml-2 text-sm text-blue-600 font-normal">
                  Analytics Overview
                </span>
              </h1>
            </div>

            {/* Date Filters */}
            <div className="border-b border-main-border">
              <div className="px-4 sm:px-6 py-4">
                <h2 className="text-sm font-medium text-gray-600 mb-3">
                  Select Time Period
                </h2>
                <div className="grid grid-cols-12 gap-y-4">
                  {/* Date Range Buttons */}
                  <div className="col-span-12 lg:col-span-8 flex flex-wrap gap-2">
                    {dateFilterOptions.map((option) => (
                      <DateFilterButton
                        key={option.id}
                        option={option}
                        selectState={selectState}
                        handleDateRangeFilter={handleDateRangeFilter}
                      />
                    ))}
                  </div>

                  {/* Custom Date Range Inputs */}
                  <div className="col-span-12 lg:col-span-4 flex flex-col sm:flex-row gap-2 sm:justify-end mt-2 lg:mt-0">
                    <div className="relative flex-1 sm:max-w-xs">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <input
                        type="date"
                        name="startDate"
                        value={startDate}
                        onChange={handleFormDate}
                        className={`border rounded-lg pl-10 pr-3 py-2 w-full focus:outline-none focus:ring-2 ${
                          selectState === "range"
                            ? "border-blue-500 text-blue-700 focus:ring-blue-500/30"
                            : "border-gray-300 text-gray-600 focus:ring-blue-500/20"
                        }`}
                      />
                    </div>
                    <div className="relative flex-1 sm:max-w-xs">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <input
                        type="date"
                        name="endDate"
                        value={endDate}
                        onChange={handleFormDate}
                        className={`border rounded-lg pl-10 pr-3 py-2 w-full focus:outline-none focus:ring-2 ${
                          selectState === "range"
                            ? "border-blue-500 text-blue-700 focus:ring-blue-500/30"
                            : "border-gray-300 text-gray-600 focus:ring-blue-500/20"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Cards */}
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h2 className="text-lg font-semibold text-gray-700 mb-4 sm:mb-0">
                  Performance Metrics
                </h2>

                {/* Delete Button Section */}
                {id && (
                  <button
                    onClick={handelDeleteTrack}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2 shadow-sm hover:shadow"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete Analytics
                  </button>
                )}
              </div>

              {/* Analytics Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {!id && (
                  <AnalyticsCard
                    label="Total Ads"
                    value={totalAds}
                    icon={<SiGoogleads size={26} />}
                    color="green"
                  />
                )}
                <AnalyticsCard
                  label="Views"
                  value={totalViews}
                  icon={<MdPreview size={26} />}
                  color="blue"
                />
                <AnalyticsCard
                  label="Clicks"
                  value={totalClicks}
                  icon={<GiClick size={26} />}
                  color="purple"
                />
              </div>
            </div>

            {/* Charts */}
            <div className="p-4 sm:p-6 pt-0">
              <h2 className="text-lg font-semibold text-gray-700 mb-4">
                Performance Charts
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {viewsData.length > 0 ? (
                  <div className="h-64 sm:h-72 md:h-100">
                    <ViewChart ViewData={viewsData} />
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-xl border border-gray-200 p-8 flex flex-col items-center justify-center">
                    <MdPreview size={40} className="text-gray-400 mb-3" />
                    <p className="text-gray-500 text-center">
                      No view data available for the selected period
                    </p>
                  </div>
                )}

                {clicksData.length > 0 ? (
                  <div className="h-64 sm:h-72 md:h-100">
                    <ClickChart ClickData={clicksData} />
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 flex flex-col items-center justify-center">
                    <GiClick size={40} className="text-gray-400 mb-3" />
                    <p className="text-gray-500 text-center">
                      No click data available for the selected period
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;
// Date Filter Button Component
const DateFilterButton = ({ option, selectState, handleDateRangeFilter }) => {
  const isActive = selectState === option.id;

  return (
    <button
      onClick={() => handleDateRangeFilter(option.id)}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
        isActive
          ? "bg-blue-600 text-white shadow-md"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-800"
      }`}
    >
      <span className={`${isActive ? "text-white" : "text-blue-600"}`}>
        {option.icon}
      </span>
      {option.label}
    </button>
  );
};

const AnalyticsCard = ({ label, value, icon, color }) => {
  const colorStyles = {
    green: {
      background: "from-green-50 to-emerald-50",
      border: "border-green-200",
      text: "text-green-800",
      iconBg: "bg-green-100 text-green-700",
      iconBorder: "border-green-200",
      shadow: "shadow-green-200/40",
    },
    blue: {
      background: "from-blue-50 to-sky-50",
      border: "border-blue-200",
      text: "text-blue-800",
      iconBg: "bg-blue-100 text-blue-700",
      iconBorder: "border-blue-200",
      shadow: "shadow-blue-200/40",
    },
    purple: {
      background: "from-purple-50 to-violet-50",
      border: "border-purple-200",
      text: "text-purple-800",
      iconBg: "bg-purple-100 text-purple-700",
      iconBorder: "border-purple-200",
      shadow: "shadow-purple-200/40",
    },
  };

  const style = colorStyles[color];

  return (
    <div
      className={`relative overflow-hidden bg-gradient-to-br ${style.background} border ${style.border} rounded-2xl p-5 shadow-sm hover:shadow-lg transition-all duration-300`}
    >
      <div className="absolute -right-6 -top-10 rounded-full w-24 h-24 bg-white/30 blur-2xl"></div>

      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <h2 className={`text-2xl sm:text-3xl font-bold mt-1 ${style.text}`}>
            {value < 10
              ? "0" + value
              : value < 100000
              ? value.toLocaleString()
              : formatView(value)}
          </h2>
        </div>
        <div
          className={`p-3 rounded-full ${style.iconBg} shadow-md ${style.shadow} border ${style.iconBorder} flex items-center justify-center`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
};
